// Companion/blocks/useStudySetLaunch.ts
//
// Leaving the thread for the full study UI (ORA-681).
//
// Four steps, in this order, and the order is the whole design:
//   1. if a session is already open, ask — there is no queueing;
//   2. launch the set server-side (which is what clamps a free student's
//      question set to the allowance they have left);
//   3. enter standalone study, carrying the way back to this exact block;
//   4. navigate.
//
// Step 3 before step 4 is the ORA-674 rule: outside standalone mode the block
// loader ignores the id in the URL and opens the daily session's active block
// instead. The way back is the persisted study context rather than location
// state, so it survives a reload in the middle of the session.

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AIDrawerContext } from '@/providers/AIDrawerContext';
import type { RootState } from '@/store/store';
import { selectActiveBlock } from '@/slices/blocks/blocksSlice';
import {
  enterStandalone,
  selectStudyMode,
} from '@/slices/studyContext/studyContextSlice';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatStudySetBlockPayload } from '@/types/chatBlocks';
import type { ChatSurface } from '@/types/chatEnvelope';
import type { ChatNavigationTarget } from '@/types/chatStream';
import { resolveChatBlockTarget } from './chatBlockTargets';
import { isMidStudySession } from './studySetSession';
import { captureStudySetEvent, studySetEventProps } from './studySetAnalytics';

export interface UseStudySetLaunchParams {
  block: ChatBlock;
  surface: ChatSurface;
}

export interface UseStudySetLaunch {
  /**
   * Run before the launch request. Resolves false when the student chose to
   * stay in the session they already had open, in which case nothing is
   * launched and the footer goes back to rest.
   */
  confirmLeave: () => Promise<boolean>;
  /** Send the student into the session a launch opened. */
  openLaunched: (
    target: ChatNavigationTarget,
    set: ChatStudySetBlockPayload
  ) => void;
  /** Props for the confirm dialog the block renders. */
  leaveModal: { open: boolean; onConfirm: () => void; onCancel: () => void };
}

export const useStudySetLaunch = ({
  block,
  surface,
}: UseStudySetLaunchParams): UseStudySetLaunch => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const studyMode = useSelector(selectStudyMode);
  const activeBlock = useSelector(selectActiveBlock);
  /**
   * Read through the context rather than `useAIDrawer`, which throws: the block
   * also renders on the chat page, which is outside the provider. Only `close`
   * is taken, because the context value changes identity on nearly every drawer
   * state change and `openLaunched` is handed down as a prop.
   */
  const closeCompanion = useContext(AIDrawerContext)?.close;

  const hasRunningFlashcards = useSelector(
    (state: RootState) =>
      state.flashcardRun.status === 'ready' &&
      state.flashcardRun.currentCardId !== null
  );

  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const answerLeave = useRef<((leaving: boolean) => void) | null>(null);

  /**
   * A block can go while its dialog is up — the thread re-renders, the drawer
   * closes, the student navigates — and an unanswered promise would leave the
   * footer spinning for the rest of the session. Unmounting means "stay".
   */
  useEffect(
    () => () => {
      const answer = answerLeave.current;
      answerLeave.current = null;
      answer?.(false);
    },
    []
  );

  const settleLeave = useCallback((leaving: boolean) => {
    setIsLeaveModalOpen(false);
    const answer = answerLeave.current;
    answerLeave.current = null;
    answer?.(leaving);
  }, []);

  const confirmLeave = useCallback(() => {
    const isMidSession = isMidStudySession({
      pathname: location.pathname,
      studyMode,
      hasUnfinishedBlock: Boolean(activeBlock) && !activeBlock?.isSubmitted,
      hasRunningFlashcards,
    });

    if (!isMidSession) return Promise.resolve(true);

    return new Promise<boolean>((resolve) => {
      // A second launch while the dialog is up would strand the first caller's
      // promise; the earlier one is answered "stay" before this one replaces it.
      answerLeave.current?.(false);
      answerLeave.current = resolve;
      setIsLeaveModalOpen(true);
    });
  }, [activeBlock, hasRunningFlashcards, location.pathname, studyMode]);

  const openLaunched = useCallback(
    (target: ChatNavigationTarget, set: ChatStudySetBlockPayload) => {
      const resolved = resolveChatBlockTarget(target);
      if (!resolved) return;

      dispatch(
        enterStandalone({
          // Only the QBank routes name a block; a flashcard or video set has
          // none, and a placeholder here would be loaded as if it were real.
          blockId: resolved.standaloneBlockId,
          returnTo: 'chat',
          returnPath: location.pathname,
          returnSearch: location.search,
          flashcardScope:
            set.kind === 'flashcard' ? { kind: 'set', setId: set.setId } : null,
          chatReturn: {
            conversationId: block.conversationId,
            blockId: block.id,
            surface,
            setId: set.setId,
            // Which runner this set belongs to: a runner must not claim a set
            // of another kind just because one is open (ORA-681 review).
            kind: set.kind,
          },
        })
      );

      captureStudySetEvent(
        'study_set_launch',
        studySetEventProps(set, surface)
      );

      // The full session is what the student asked for, so the companion gets
      // out of its way: the drawer and the floating shell share one `isOpen`,
      // which nothing persists, so a close here survives the navigation. The
      // way back (`studyFlow/studySetReturn.ts`) reopens it on this block. The
      // chat page is not the companion's chrome — closing there would shut the
      // thread the student launched from.
      if (surface !== 'chat_page') closeCompanion?.();

      navigate(resolved.to);
    },
    [
      block.conversationId,
      block.id,
      closeCompanion,
      dispatch,
      location.pathname,
      location.search,
      navigate,
      surface,
    ]
  );

  return {
    confirmLeave,
    openLaunched,
    leaveModal: {
      open: isLeaveModalOpen,
      onConfirm: () => settleLeave(true),
      onCancel: () => settleLeave(false),
    },
  };
};
